import React, { forwardRef, useRef, useState } from "react";
import YearJumpPopover from "./YearJumpPopover";
import { nativeDatePickerSupportsYearJump } from "./nativeYearJump";
import { ymdToUs } from "./dateParts";

const supportsNativeYearJump = nativeDatePickerSupportsYearJump();

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
  </svg>
);

/**
 * Drop-in for `<input type="date">`.
 * Chrome and Brave keep the native control. Safari gets a calendar that can
 * jump straight to a year. `onChange` still receives YYYY-MM-DD.
 */
const DateInput = forwardRef(function DateInput(
  {
    type: _type,
    className = "",
    value,
    onChange,
    onBlur,
    min,
    max,
    disabled = false,
    readOnly = false,
    name,
    id,
    required = false,
    onClick,
    onKeyDown,
    placeholder,
    ...rest
  },
  ref,
) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const ymd = typeof value === "string" ? value.slice(0, 10) : value || "";

  if (supportsNativeYearJump) {
    return (
      <input
        ref={ref}
        type="date"
        className={className}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        min={min}
        max={max}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
        id={id}
        required={required}
        onClick={onClick}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        {...rest}
      />
    );
  }

  const openCalendar = () => {
    if (disabled || readOnly) return;
    setOpen(true);
  };

  const emit = (next) => {
    onChange?.({
      target: { name, value: next },
      currentTarget: { name, value: next },
    });
  };

  const fullWidth = /\bw-full\b/.test(className || "");

  return (
    <div ref={anchorRef} className={fullWidth ? "relative min-w-0 w-full" : "relative inline-block max-w-full align-middle"}>
      <input type="hidden" name={name} value={ymd} readOnly />
      <input
        {...rest}
        ref={ref}
        id={id}
        type="text"
        inputMode="none"
        readOnly
        required={required}
        disabled={disabled}
        value={ymdToUs(ymd)}
        placeholder={placeholder || "MM/DD/YYYY"}
        onBlur={onBlur}
        onClick={(event) => {
          onClick?.(event);
          openCalendar();
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
            event.preventDefault();
            openCalendar();
          }
        }}
        aria-haspopup="dialog"
        className={`${className} cursor-pointer pr-8`}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled || readOnly}
        onClick={openCalendar}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Open calendar"
      >
        <CalendarIcon />
      </button>
      <YearJumpPopover
        open={open}
        anchorRef={anchorRef}
        value={ymd}
        min={min}
        max={max}
        onClose={() => setOpen(false)}
        onSelect={(next) => {
          emit(next);
          setOpen(false);
        }}
      />
    </div>
  );
});

export default DateInput;
